import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { CARRIERS, ORDER_STATUS } from "../../utils/constants";
import { get, isAdmin } from "../../utils/io";
import { Protected } from "../Authz/Protected";
import { useQuery } from "../hooks";
import MainContentContainer from "../Layouts/MainContentContainer";
import OrderModal from "./OrderModal";
import { orderStatusStyles } from "utils/theme";

const emptySearchInputs = {
  id: "",
  hubId: "",
  trackingId: "",
  carrier: "",
  status: "",
  orgAlias: "",
};

function OrderSearchForm({ onSubmit, isSearchDisabled }) {
  const [searchInputs, setSearchInputs] = useState({ ...emptySearchInputs });

  const changeSearchInput = (e) => {
    const { name, value } = e.target;
    setSearchInputs({ ...searchInputs, [name]: value });
  };

  const submitForm = async (e) => {
    e.preventDefault();
    onSubmit({ ...searchInputs });
  };

  const clearFilters = async (e) => {
    e.preventDefault();
    setSearchInputs({ ...emptySearchInputs });
    onSubmit({ ...emptySearchInputs });
  };

  const isClearFiltersDisabled = () =>
    Object.values(searchInputs).reduce((prev, next) => prev && !next, true);

  return (
    <form onSubmit={submitForm}>
      <Box
        sx={{
          marginBottom: "15px",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <TextField
          label="ID"
          onChange={changeSearchInput}
          value={searchInputs.id}
          size="small"
          variant="standard"
          sx={{
            marginBottom: "15px",
            marginRight: "15px",
          }}
          name="id"
        />
        <TextField
          label="Hub ID"
          onChange={changeSearchInput}
          value={searchInputs.hubId}
          size="small"
          variant="standard"
          sx={{
            marginBottom: "15px",
            marginRight: "15px",
          }}
          name="hubId"
        />
        {isAdmin() && (
          <TextField
            label="Organization Alias"
            onChange={changeSearchInput}
            value={searchInputs.orgAlias}
            size="small"
            variant="standard"
            sx={{
              marginBottom: "15px",
              marginRight: "15px",
            }}
            name="orgAlias"
          />
        )}
        <TextField
          label="Tracking #"
          onChange={changeSearchInput}
          value={searchInputs.trackingId}
          size="small"
          variant="standard"
          sx={{
            marginBottom: "15px",
            marginRight: "15px",
          }}
          name="trackingId"
        />
        <FormControl
          variant="standard"
          sx={{
            marginBottom: "15px",
            marginRight: "15px",
            minWidth: "120px",
          }}
        >
          <InputLabel>Carrier</InputLabel>
          <Select
            value={searchInputs.carrier}
            onChange={changeSearchInput}
            label="Carrier"
            name="carrier"
          >
            {CARRIERS.map((carrier) => (
              <MenuItem key={carrier.value} value={carrier.value}>
                {carrier.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          variant="standard"
          sx={{
            marginBottom: "15px",
            marginRight: "15px",
            minWidth: "120px",
          }}
        >
          <InputLabel>Status</InputLabel>
          <Select
            value={searchInputs.status}
            onChange={changeSearchInput}
            label="Status"
            name="status"
          >
            {Object.values(ORDER_STATUS).map((status) => (
              <MenuItem key={status} value={status}>
                {status.toLocaleUpperCase()}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          color="error"
          variant="contained"
          onClick={clearFilters}
          sx={{ marginRight: "15px" }}
          disabled={isClearFiltersDisabled()}
        >
          Clear Filters
        </Button>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          onClick={submitForm}
          disabled={isSearchDisabled}
        >
          Search
        </Button>
      </Box>
    </form>
  );
}

OrderSearchForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isSearchDisabled: PropTypes.bool,
};

OrderSearchForm.defaultProps = {
  isSearchDisabled: false,
};

function Orders() {
  const [orders, setOrders] = useState([]);
  const [searchFields, setSearchFields] = useState(emptySearchInputs);
  const [isCreate, setIsCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orderSelected, setOrderSelected] = useState({});

  const { enqueueSnackbar } = useSnackbar();

  const history = useHistory();
  const query = useQuery();

  useEffect(() => {
    const orderId = query.get("orderId");
    if (!orderId || !isAdmin()) return;
    const fetchAndOpen = async () => {
      const res = await get(`admin/orders/${orderId}`);
      if (res.status === 200) {
        setOrderSelected(res.data);
        history.replace("/hub-orders");
      } else {
        enqueueSnackbar("Unable to retrieve order", { variant: "error" });
      }
    };
    fetchAndOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    { field: "id", headerName: "ID", flex: 1 },
    ...(isAdmin()
      ? [
          {
            field: "org",
            headerName: "Org Alias",
            flex: 0.75,
            renderCell: ({ value }) => value.alias,
          },
        ]
      : []),
    {
      field: "status",
      headerName: "Status",
      flex: 1.5,
      renderCell: (param) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <Box
            sx={{
              padding: "5px 7px",
              borderRadius: "4px",
              textTransform: "uppercase",
              ...orderStatusStyles[param.value.replace(" ", "_")],
            }}
          >
            {param.value}
          </Box>
        </Box>
      ),
    },
    {
      field: "request_type",
      headerName: "Request Type",
      width: 150,
      renderCell: (params) => {
        const {
          exemption_type: exemptionType,
          has_return: hasReturn,
          is_new_order: isNewOrder,
        } = params.row;

        if (exemptionType === "RETURNS" || hasReturn)
          return (
            <Chip
              label="Return Request"
              color="warning"
              size="small"
              variant="outlined"
            />
          );
        if (exemptionType === "FREE_SAMPLE")
          return (
            <Chip
              label="Free Sample"
              color="success"
              size="small"
              variant="outlined"
            />
          );
        if (exemptionType === "STARTING_STOCK")
          return (
            <Chip
              label="Starting Stock"
              color="info"
              size="small"
              variant="outlined"
            />
          );
        if (isNewOrder)
          return (
            <Chip
              label="New Order"
              color="primary"
              size="small"
              variant="outlined"
            />
          );
        return null;
      },
    },
    {
      field: "created_time",
      headerName: "Created Time",
      flex: 1.2,
      renderCell: ({ value }) => new Date(value).toLocaleString(),
    },
    {
      field: "created_by",
      headerName: "Created by",
      flex: 1,
      renderCell: ({ value }) =>
        value?.first_name && value?.last_name
          ? `${value.first_name} ${value.last_name}`
          : `${value?.username || "-"}`,
    },
    {
      field: "number_of_hubs",
      headerName: "Requested Hubs",
      flex: 1,
      renderCell: (param) => (
        <Box>
          <Chip color="primary" label={`${param.value}`} />
        </Box>
      ),
    },
    ...(isAdmin()
      ? [
          {
            field: "hub_count",
            headerName: "Hub Count",
            flex: 0.75,
            renderCell: (param) => (
              <Box>
                <Chip color="primary" label={`${param.value}`} />
              </Box>
            ),
          },
        ]
      : []),
  ];

  const getOrderList = async () => {
    setLoading(true);

    const params = {
      order_id: searchFields.id,
      tracking_number: searchFields.trackingId,
      carrier: searchFields.carrier,
      hub_id: searchFields.hubId,
      status: searchFields.status,
    };

    const res = isAdmin()
      ? await get("admin/orders", {
          ...params,
          org_alias: searchFields.orgAlias,
        })
      : await get("/hub_orders", params);

    if (res.status === 200) {
      setOrders(res.data.orders);
    } else {
      setOrders([]);
    }

    setLoading(false);
  };

  const onOpenOrderModal = () =>
    isAdmin() ? setIsCreate(true) : history.push("/hub-orders/request");

  const onCloseDetailOrder = async () => {
    setOrderSelected({});
    await getOrderList();
  };

  const onCloseOrderModal = () => {
    setIsCreate(false);
    onCloseDetailOrder();
  };

  const onRowClick = async (row) => {
    if (isAdmin()) {
      const res = await get(`admin/orders/${row.id}`);
      return res.status !== 200
        ? enqueueSnackbar("Unable to retrieve order", { variant: "error" })
        : setOrderSelected(res.data);
    }
    return history.push(`/hub-orders/${row.id}`);
  };

  useEffect(() => {
    getOrderList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFields]);

  return (
    <MainContentContainer title="Hub Orders" isEnterpriseOnly>
      <Grid container direction="column">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <OrderSearchForm
            onSubmit={setSearchFields}
            isSearchDisabled={loading}
          />
        </Box>

        <Protected permission="CreateHubOrders">
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              permission="CreateHubOrders"
              sx={{ height: "fit-content", mb: "15px" }}
              color="primary"
              variant="contained"
              onClick={onOpenOrderModal}
              startIcon=<AddIcon />
            >
              {isAdmin() ? "Create" : "Request"}
            </Button>
          </Box>
        </Protected>
        <DataGrid
          columns={columns}
          rows={orders}
          autoHeight
          loading={loading}
          onRowClick={onRowClick}
          sx={{
            ".MuiDataGrid-row": {
              cursor: "pointer",
            },
          }}
        />
        {isCreate && (
          <OrderModal isModal onCloseOrderModal={onCloseOrderModal} />
        )}
        {orderSelected.id && (
          <OrderModal
            isModal
            onCloseOrderModal={onCloseDetailOrder}
            row={orderSelected}
          />
        )}
      </Grid>
    </MainContentContainer>
  );
}
export default Orders;
